import { Link, NavLink } from 'react-router-dom'
import { useState } from 'react'
import CountdownTimer from './CountdownTimer.jsx'
import LivePulseDot from './LivePulseDot.jsx'
import { useParticipant, signOut } from '../hooks/useParticipant.js'
import { useRealtimeLeaderboard } from '../hooks/useRealtimeLeaderboard.js'
import { useFirstVisit } from '../hooks/useFirstVisit.js'
import { isEnabled, toggleEnabled } from '../lib/sounds.js'

/**
 * Persistent header. Logo, nav, countdown, live pulse, your rank pill, user menu.
 */
export default function BrandShell({ children, hideHeader = false }) {
  const { participant } = useParticipant()
  const { rows } = useRealtimeLeaderboard()
  const { restartTour } = useFirstVisit()
  const [menuOpen, setMenuOpen] = useState(false)
  const [soundOn, setSoundOn] = useState(isEnabled())

  const myRank = participant ? rows.findIndex(r => r.participant.id === participant.id) + 1 : null

  return (
    <div className="min-h-screen flex flex-col">
      {!hideHeader && (
        <header className="aurora-bg sticky top-0 z-30 border-b border-brand-border bg-white/85 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src="/climatise-logo.svg" alt="Climatise" className="h-7 w-auto" />
              <span className="font-display text-lg text-brand-navy hidden sm:inline">LinkedIn league</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1 ml-4">
              <NavItem to="/">Leaderboard</NavItem>
              <NavItem to="/me">You</NavItem>
              <NavItem to="/log">Log</NavItem>
              {participant?.is_admin && <NavItem to="/admin">Admin</NavItem>}
            </nav>

            <div className="ml-auto flex items-center gap-3">
              <div className="hidden lg:flex items-center gap-3">
                <LivePulseDot label="Live" />
                <span className="w-px h-4 bg-brand-border" />
                <CountdownTimer compact />
              </div>

              {myRank > 0 && (
                <Link to="/me" className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-blue-tint text-brand-blue text-xs font-semibold hover:bg-brand-blue hover:text-white transition-colors">
                  <span>#{myRank}</span>
                  <span className="opacity-70">your rank</span>
                </Link>
              )}

              <div className="relative">
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  className="w-8 h-8 rounded-full bg-brand-blue text-white flex items-center justify-center text-xs font-bold hover:bg-brand-blue-hover"
                  aria-label="User menu"
                >
                  {participant?.name?.split(' ').map(p => p[0]).slice(0, 2).join('') || '?'}
                </button>
                {menuOpen && (
                  <>
                    {/* Backdrop catches outside clicks so the menu actually closes */}
                    <button
                      type="button"
                      aria-label="Close menu"
                      onClick={() => setMenuOpen(false)}
                      className="fixed inset-0 z-[55] cursor-default"
                    />
                    <div className="absolute right-0 top-10 w-56 bg-white border border-brand-border rounded-xl shadow-card-hover py-1 text-sm z-[60]">
                      <div className="px-4 py-2 border-b border-brand-border">
                        <div className="font-semibold text-brand-text">{participant?.name}</div>
                        <div className="text-xs text-brand-text-muted">{participant?.email}</div>
                      </div>
                      <MenuItem onClick={() => { restartTour(); setMenuOpen(false) }}>Replay tour</MenuItem>
                      <MenuItem onClick={() => { setSoundOn(toggleEnabled()); }}>
                        Sound: {soundOn ? 'on' : 'off'}
                      </MenuItem>
                      <Link to="/tv" className="block px-4 py-2 hover:bg-brand-bg" onClick={() => setMenuOpen(false)}>TV mode ↗</Link>
                      <Link to="/activate/connect" className="block px-4 py-2 hover:bg-brand-bg text-brand-text-muted" onClick={() => setMenuOpen(false)}>Connect LinkedIn</Link>
                      <div className="border-t border-brand-border mt-1">
                        <MenuItem onClick={signOut} danger>Sign out</MenuItem>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      <main className="flex-1">
        {children}
      </main>

      <footer className="text-center text-xs text-brand-text-muted py-6">
        Climatise · Q2 2026
      </footer>
    </div>
  )
}

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isActive ? 'bg-brand-blue-tint text-brand-blue' : 'text-brand-text hover:bg-brand-bg'
        }`
      }
    >
      {children}
    </NavLink>
  )
}

function MenuItem({ onClick, children, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-2 hover:bg-brand-bg ${danger ? 'text-brand-danger' : 'text-brand-text'}`}
    >
      {children}
    </button>
  )
}
